import { useState } from 'react'

import { Button } from '@/components/ui/Button'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { StatusPill } from '@/components/ui/StatusPill'
import { useToast } from '@/components/ui/toastContext'
import { CandidateDetailDrawer } from '@/features/recruiter/components/CandidateDetailDrawer'
import type { CandidateTask, CandidateTaskStatus } from '@/features/recruiter/api/candidates.api'
import { useDeleteCandidateTask, useMyCandidateTasks, useUpdateCandidateTask } from '@/features/recruiter/hooks/useCandidates'
import { formatDateTime, humanizeEnum } from '@/utils/format'

type ScopeFilter = 'mine' | 'all'
type StatusFilter = CandidateTaskStatus | ''

export function MyTasksPage() {
  const [scope, setScope] = useState<ScopeFilter>('mine')
  const [status, setStatus] = useState<StatusFilter>('pending')
  const [selectedProfileId, setSelectedProfileId] = useState<number | null>(null)

  const { data: tasks, isPending } = useMyCandidateTasks({
    assigned_to_me: scope === 'mine',
    status: status || undefined,
  })
  const updateTask = useUpdateCandidateTask()
  const deleteTask = useDeleteCandidateTask()
  const toast = useToast()

  const toggleDone = (task: CandidateTask) => {
    updateTask.mutate(
      { id: task.id, status: task.status === 'done' ? 'pending' : 'done' },
      {
        onSuccess: () => toast.success(task.status === 'done' ? 'Reminder reopened.' : 'Reminder marked done.'),
        onError: () => toast.error('Could not update reminder. Please try again.'),
      },
    )
  }

  const remove = (task: CandidateTask) => {
    deleteTask.mutate(task.id, {
      onSuccess: () => toast.success('Reminder removed.'),
      onError: () => toast.error('Could not remove reminder. Please try again.'),
    })
  }

  const columns: DataTableColumn<CandidateTask>[] = [
    {
      key: 'candidate',
      header: 'Candidate',
      render: (row) => (
        <button
          type="button"
          className="btn btn-link btn-sm p-0"
          onClick={() => setSelectedProfileId(row.studentProfileId)}
        >
          {row.candidateName ?? `Candidate #${row.studentProfileId}`}
        </button>
      ),
    },
    { key: 'type', header: 'Type', render: (row) => humanizeEnum(row.type) },
    { key: 'due', header: 'Due', render: (row) => formatDateTime(row.dueAt) },
    { key: 'note', header: 'Note', render: (row) => row.note || '—', className: 'text-muted' },
    { key: 'assignee', header: 'Assigned to', render: (row) => row.assignedTo?.name ?? '—' },
    { key: 'status', header: 'Status', render: (row) => <StatusPill status={row.status} /> },
    {
      key: 'actions',
      header: '',
      render: (row) => (
        <div className="d-flex gap-2 justify-content-end">
          <Button variant="secondary" onClick={() => toggleDone(row)} loading={updateTask.isPending}>
            {row.status === 'done' ? 'Reopen' : 'Mark done'}
          </Button>
          <Button
            variant="danger"
            confirmTitle="Remove this reminder?"
            onClick={() => remove(row)}
            loading={deleteTask.isPending}
          >
            Delete
          </Button>
        </div>
      ),
      className: 'text-end',
    },
  ]

  return (
    <>
      <div className="my-4 d-flex align-items-center justify-content-between flex-wrap gap-2 page-header-breadcrumb">
        <p className="fw-semibold fs-20 mb-0">My Tasks</p>
      </div>

      <div className="card custom-card mb-3">
        <div className="card-body d-flex flex-wrap gap-3">
          <div className="d-flex align-items-center gap-2">
            <label htmlFor="task-scope" className="fs-13 text-muted mb-0">
              Show
            </label>
            <select
              id="task-scope"
              className="form-select form-select-sm"
              value={scope}
              onChange={(e) => setScope(e.target.value as ScopeFilter)}
            >
              <option value="mine">Assigned to me</option>
              <option value="all">All org reminders</option>
            </select>
          </div>
          <div className="d-flex align-items-center gap-2">
            <label htmlFor="task-status" className="fs-13 text-muted mb-0">
              Status
            </label>
            <select
              id="task-status"
              className="form-select form-select-sm"
              value={status}
              onChange={(e) => setStatus(e.target.value as StatusFilter)}
            >
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="done">Done</option>
            </select>
          </div>
        </div>
      </div>

      <div className="card custom-card">
        <DataTable
          columns={columns}
          rows={tasks ?? []}
          rowKey={(row) => row.id}
          loading={isPending}
          emptyMessage="No reminders here. Set one from a candidate's search result."
        />
      </div>

      <CandidateDetailDrawer
        show={selectedProfileId != null}
        onClose={() => setSelectedProfileId(null)}
        studentProfileId={selectedProfileId ?? undefined}
      />
    </>
  )
}
